import { Dialog, Portal } from "@ark-ui/react";
import React from "react";
import { create } from "zustand";

import { css } from "../styled-system/css";

import { useTranslate } from "./translation";

type Item = {
  color: "information" | "error" | "success" | "warning";
  content: React.ReactNode;
  title: string;
} & (
  | {
      ok?: string;
      resolve: { (value?: undefined): void };
      type: "alert";
    }
  | {
      no?: string;
      resolve: { (value?: boolean): void };
      type: "confirm";
      yes?: string;
    }
);

type DialogManager = {
  items: Array<Item>;

  alert: { (item: Omit<Extract<Item, { type: "alert" }>, "resolve" | "type">): Promise<undefined> };
  confirm: {
    (
      item: Omit<Extract<Item, { type: "confirm" }>, "resolve" | "type">,
    ): Promise<boolean | undefined>;
  };
  setItems: { (items: Array<Item> | { (items: Array<Item>): Array<Item> }): void };
};

export const useDialogManager = create<DialogManager>(function (set) {
  return {
    items: [],

    alert(item) {
      const { promise, resolve } = Promise.withResolvers<undefined>();
      set(function ({ items }) {
        return {
          items: items.concat([{ ...item, resolve, type: "alert" }]),
        };
      });
      return promise;
    },

    confirm(item) {
      const { promise, resolve } = Promise.withResolvers<boolean | undefined>();
      set(function ({ items }) {
        return {
          items: items.concat([{ ...item, resolve, type: "confirm" }]),
        };
      });
      return promise;
    },

    setItems(items) {
      set(function (state) {
        return { items: typeof items === "function" ? items(state.items) : items };
      });
    },
  };
});

export const $alert = useDialogManager.getState().alert;
export const $confirm = useDialogManager.getState().confirm;

export function DialogDisplay() {
  const translate = useTranslate();

  const items = useDialogManager(function (state) {
    return state.items;
  });
  const setItems = useDialogManager(function (state) {
    return state.setItems;
  });

  const item = items.at(0);

  let color;
  switch (item?.color) {
    case "error":
      color = "#DC2626";
      break;
    case "information":
      color = "#0284C7";
      break;
    case "success":
      color = "#0D9488";
      break;
    case "warning":
      color = "#CA8A04";
      break;
    default:
      color = "#3B82F6";
      break;
  }

  return (
    <Dialog.Root
      onEscapeKeyDown={function (event) {
        console.debug(event);
        item?.resolve();
        setItems(function (items) {
          return items.filter(function (i) {
            return i !== item;
          });
        });
      }}
      open={Boolean(item)}
    >
      <Portal>
        <Dialog.Backdrop
          className={css({
            backdropFilter: "blur(2px)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            position: "fixed",
            inset: 0,
            zIndex: 5000,
          })}
        />
        <Dialog.Positioner
          className={css({
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            padding: { smDown: "16px", sm: "24px" },
            position: "fixed",
            inset: 0,
            zIndex: 5001,
          })}
        >
          <Dialog.Content
            className={css({
              backgroundColor: "background",
              borderColor: "border",
              borderStyle: "solid",
              borderWidth: "1px",
              borderRadius: "8px",
              boxShadow: "0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.1)",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              overflow: "auto",
              padding: "16px",
              position: "relative",
              width: "stretch",
              maxHeight: "stretch",
              maxWidth: "400px",
              zIndex: 5001,
            })}
          >
            <Dialog.Title className={css({ fontSize: "18px", fontWeight: 600 })} style={{ color }}>
              {item?.title}
            </Dialog.Title>

            {typeof item?.content === "string" ? (
              <Dialog.Description
                className={css({
                  flexGrow: 1,
                  fontSize: "14px",
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                })}
              >
                {item.content}
              </Dialog.Description>
            ) : (
              item?.content
            )}

            <div
              className={css({
                display: "flex",
                flexDirection: { smDown: "column", sm: "row" },
                justifyContent: { smDown: "stretch", sm: "flex-end" },
                gap: "8px",
              })}
            >
              {item?.type === "alert" && (
                <button
                  className={css({
                    alignItems: "center",
                    cursor: "pointer",
                    backgroundColor: "#18181B",
                    color: "#FAFAFA",
                    display: "inline-flex",
                    fontSize: "14px",
                    fontWeight: 500,
                    justifyContent: "center",
                    lineHeight: 1,
                    transition: "all 0.2s ease-in-out",
                    minWidth: 0,
                    overflow: "clip",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    padding: "10px 16px",
                    borderRadius: "4px",
                    _disabled: { cursor: "not-allowed", opacity: 0.5 },
                    _hover: { opacity: 0.7 },
                  })}
                  onClick={function () {
                    item?.resolve();
                    setItems(function (items) {
                      return items.filter(function (i) {
                        return i !== item;
                      });
                    });
                  }}
                >
                  {item.ok ?? translate("%Ok.0%")}
                </button>
              )}

              {item?.type === "confirm" && (
                <button
                  className={css({
                    alignItems: "center",
                    cursor: "pointer",
                    backgroundColor: "#18181B",
                    color: "#FAFAFA",
                    display: "inline-flex",
                    fontSize: "14px",
                    fontWeight: 500,
                    justifyContent: "center",
                    lineHeight: 1,
                    transition: "all 0.2s ease-in-out",
                    minWidth: 0,
                    overflow: "clip",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    padding: "10px 16px",
                    borderRadius: "4px",
                    _disabled: { cursor: "not-allowed", opacity: 0.5 },
                    _hover: { opacity: 0.7 },
                  })}
                  onClick={function () {
                    item?.resolve(true);
                    setItems(function (items) {
                      return items.filter(function (i) {
                        return i !== item;
                      });
                    });
                  }}
                >
                  {item.yes ?? translate("%Ok.0%")}
                </button>
              )}

              {item?.type === "confirm" && (
                <button
                  className={css({
                    alignItems: "center",
                    cursor: "pointer",
                    borderStyle: "solid",
                    borderWidth: "1px",
                    backgroundColor: "white",
                    borderColor: "#E4E4E780",
                    color: "#18181B",
                    display: "inline-flex",
                    fontSize: "14px",
                    fontWeight: 500,
                    justifyContent: "center",
                    lineHeight: 1,
                    transition: "all 0.2s ease-in-out",
                    minWidth: 0,
                    overflow: "clip",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    padding: "10px 16px",
                    borderRadius: "4px",
                    _disabled: { cursor: "not-allowed", opacity: 0.5 },
                    _hover: { opacity: 0.7 },
                  })}
                  onClick={function () {
                    item?.resolve(false);
                    setItems(function (items) {
                      return items.filter(function (i) {
                        return i !== item;
                      });
                    });
                  }}
                >
                  {item.no ?? translate("%Cancel.0%")}
                </button>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
